import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  createStyles,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  makeStyles,
  MenuItem,
  Popover,
  PopoverOrigin,
  Theme,
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import { useTranslation } from '@fluentsms/i18n';
import React, { useCallback, useEffect, useState } from 'react';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menu: {
      maxHeight: theme.spacing(44),
      overflowY: 'unset',
      '& .MuiPopover-paper': { overflowY: 'hidden' },
    },
    itemsContent: {
      overflowY: 'auto',
      padding: theme.spacing(0),
      maxHeight: theme.spacing(31.5),
      maxWidth: theme.spacing(125),
      minWidth: theme.spacing(50),
      '& li:first-child': {
        paddingTop: theme.spacing(1.5),
      },
      '& li:last-child': {
        paddingBottom: theme.spacing(1.5),
      },
    },
    checkboxPadding: {
      padding: theme.spacing(0, 1),
    },
    actionItems: {
      padding: theme.spacing(1.25, 3),
      display: 'flex',
      justifyContent: 'space-between',
    },
    checkboxSkeleton: {
      height: theme.spacing(3),
      margin: theme.spacing(0, 2),
    },
  }),
);

export interface SelectionMenuProps<T> {
  isOpen: boolean;
  anchorEl: null | HTMLElement;
  options: T[];
  onSubmit: (selectedItems: T[]) => Promise<void> | void;
  onClose: () => void;
  submitButtonText: string;
  getKeyFromOption?: (option: T) => string;
  renderCheckboxLabel?: (option: T) => JSX.Element | string;
  selectAllOnOpen?: boolean;
  loading?: boolean;
  error?: { message: string };
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  'data-testid'?: string;
}

export const SelectionMenu = <T,>(props: SelectionMenuProps<T>) => {
  const {
    isOpen = false,
    options,
    anchorEl = null,
    submitButtonText = 'Submit',
    onClose,
    renderCheckboxLabel,
    onSubmit,
    getKeyFromOption = (option: T) => (option as unknown) as string,
    selectAllOnOpen = false,
    loading = false,
    error,
    anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
    transformOrigin = { vertical: 'top', horizontal: 'right' },
  } = props;
  const { t } = useTranslation('common');
  const classes = useStyles();
  const [selectedOptions, setSelectedOptions] = useState<Map<T, boolean>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectAll = useCallback(
    (selectAll: boolean) => {
      if (selectAll) {
        setSelectedOptions(options.reduce((acc, option) => acc.set(option, true), new Map<T, boolean>()));
      } else {
        setSelectedOptions(new Map());
      }
    },
    [options],
  );

  useEffect(() => {
    if (selectAllOnOpen) {
      handleSelectAll(true);
    } else {
      handleSelectAll(false);
    }
  }, [isOpen, handleSelectAll, selectAllOnOpen]);

  const handleItemSelected = (item: T) => {
    const currSelectedItems = new Map(selectedOptions);

    if (currSelectedItems.has(item)) {
      currSelectedItems.delete(item);
    } else {
      currSelectedItems.set(item, true);
    }
    setSelectedOptions(currSelectedItems);
  };

  const areAllSelected = selectedOptions.size === options.length;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit(Array.from(selectedOptions.keys()));
    setIsSubmitting(false);
  };

  return (
    <Popover
      data-testid={props['data-testid'] || 'selection-menu'}
      anchorEl={anchorEl}
      keepMounted
      open={isOpen}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      className={classes.menu}
    >
      <Card data-testid={`${props['data-testid'] || 'selection-menu'}-container`}>
        <CardContent className={classes.itemsContent}>
          {loading &&
            Array.from(Array(options.length || 4)).map((_, index) => (
              <MenuItem disabled key={`skeleton${index}`}>
                <Grid item xs={12}>
                  <Skeleton data-testid="skeleton" variant="text" className={classes.checkboxSkeleton} />
                </Grid>
              </MenuItem>
            ))}

          {error && (
            <Alert severity="error" square>
              {error.message}
            </Alert>
          )}
          {!loading &&
            !error &&
            options.map((option) => (
              <MenuItem key={getKeyFromOption(option)} onClick={() => handleItemSelected(option)}>
                <FormControlLabel
                  onClick={(e) => e.preventDefault()}
                  control={
                    <Checkbox
                      checked={!!selectedOptions.get(option)}
                      color="primary"
                      onChange={(e) => e.preventDefault()}
                      data-testid={`${props['data-testid'] || 'option'}-checkbox-${getKeyFromOption(option)}`}
                      className={classes.checkboxPadding}
                    />
                  }
                  label={renderCheckboxLabel ? renderCheckboxLabel(option) : option}
                />
              </MenuItem>
            ))}
        </CardContent>
        {isSubmitting ? <LinearProgress /> : <Divider />}
        <CardActions className={classes.actionItems}>
          <Button
            color="primary"
            variant="text"
            onClick={() => handleSelectAll(!areAllSelected)}
            data-testid={!areAllSelected ? 'select-all-options-button' : 'deselect-all-options-button'}
            disabled={loading || !!error}
          >
            {!areAllSelected ? 'Select All' : 'Deselect All'}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={error ? onClose : handleSubmit}
            data-testid="submit-selection-menu-button"
            disabled={!error && (selectedOptions.size === 0 || loading)}
          >
            {error ? t('close') : submitButtonText}
          </Button>
        </CardActions>
      </Card>
    </Popover>
  );
};
